import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { Router, RouterLink } from '@angular/router';
import {
  DashboardService,
  DashboardProject,
  DashboardProperty,
  DashboardReservation,
} from '../../../core/services/dashboard.service';
import { OffersService } from '../../../core/services/offers.service';
import {
  PostSaleService,
  PostSaleSummary,
} from '../../../core/services/post-sale.service';
import { PropertiesService } from '../../../core/services/properties.service';
import { Property } from '../../../core/models/property.model';
import { CurrencyService } from '../../../core/services/currency.service';
import { Store } from '@ngrx/store';
import { selectCurrentUser } from '../../../store/auth/auth.selectors';
import { Subscription } from 'rxjs';
import {
  buildPropertyLink,
  downloadCsv,
  downloadXlsx,
  prependRowNumbers,
} from '../../../shared/utils/csv-export.util';
import {
  canViewPortfolioCommission,
  haspartnerWorkspaceAccessPermission,
  hasPropertyCreatePermission,
  hasSalesTeamWorkspaceAccessPermission,
  permissionSetFromRaw,
} from '../../../shared/utils/permissions.util';
import {
  propertyStatusKey,
  propertyStatusLabel,
  shouldShowPropertyStatusBadge,
} from '../../../shared/utils/property-status.util';
import {
  leadStatusLabel,
  normalizeLeadStatus,
} from '../../../shared/utils/lead-status.util';
import { resolvePostSaleFollowUpEnabled } from '../../../core/config/feature-flags';
import { ExportReportMenuComponent } from '../../../shared/components/export-report-menu/export-report-menu.component';
import { ReportExportFormat, downloadPdfReport } from '../../../shared/utils/report-export.util';
import { ReportExportsService } from '../../../core/services/report-exports.service';
import { ProjectsService } from '../../../core/services/projects.service';
import { Project } from '../../../core/models/project.model';

@Component({
  selector: 'app-developer-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideIconsModule, RouterLink, ExportReportMenuComponent],
  templateUrl: './developer-dashboard.component.html',
  styleUrls: ['./developer-dashboard.component.scss'],
})
export class DeveloperDashboardComponent implements OnInit, OnDestroy {
  private dashboardService = inject(DashboardService);
  private offersService = inject(OffersService);
  private postSaleService = inject(PostSaleService);
  private propertiesService = inject(PropertiesService);
  private router = inject(Router);
  private currencyService = inject(CurrencyService);
  private store = inject(Store);
  private reportExports = inject(ReportExportsService);
  private projectsService = inject(ProjectsService);
  private currencySub: ReturnType<
    typeof this.currencyService.currencyChanges$.subscribe
  > | null = null;
  private userSub: Subscription | null = null;
  postSaleFollowUpEnabled = true;
  reportProjects: Project[] = [];
  reportProjectId = '';
  selectedProjectId = '';
  reportProjectsLoading = false;
  dashboardError: string | null = null;
  private dashboardRequestVersion = 0;

  user$ = this.store.select(selectCurrentUser);
  @ViewChild('statusSection') statusSectionRef?: ElementRef<HTMLElement>;
  permissionCodes: string[] = [];
  actorRoleCode = '';
  todayLabel = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date());

  range: '7d' | '30d' | 'ytd' = 'ytd';
  trendLabel = 'YTD';
  private lastPayload: any | null = null;

  stats: Array<{
    label: string;
    statusKey:
      'total' | 'available' | 'reserved' | 'reservedwithdeposit' | 'sold';
    value: number | string;
    change: string;
    trend: 'up' | 'down' | 'neutral';
    metaLabel?: string;
    icon: string;
    link: string;
    amount?: number | null;
    sparkline?: number[];
  }> = [];
  loading = true;
  skeletonArray = Array.from({ length: 5 }).map((_, i) => i);

  quickActions: Array<{
    title: string;
    description: string;
    icon: string;
    link: string;
    color: string;
    requiresPropertyCreate?: boolean;
    requiresLeadAccess?: boolean;
    requirespartnerAccess?: boolean;
    requiresSalesTeamAccess?: boolean;
    requiresProjectAccess?: boolean;
    requiresProjectWrite?: boolean;
    requiresOffersAccess?: boolean;
    requiresSettingsAccess?: boolean;
    requiresMissingOfferRequirements?: boolean;
  }> = [
    {
      title: 'Manage Leads',
      description: 'View and manage all your property leads',
      icon: 'users',
      link: '/developer/leads',
      color: 'from-[#3A4E49] to-[#6E9BAD]',
      requiresLeadAccess: true,
    },
    {
      title: 'Manage Partners',
      description: 'Invite partner companies and manage inventory grants',
      icon: 'user-plus',
      link: '/developer/partners',
      color: 'from-[#6E9BAD] to-[#AA9072]',
      requirespartnerAccess: true,
    },
    {
      title: 'Manage Team',
      description: 'Manage team access and assignments',
      icon: 'users',
      link: '/developer/sales-team',
      color: 'from-[#AA9072] to-[#3A4E49]',
      requiresSalesTeamAccess: true,
    },
    {
      title: 'Offers',
      description: 'Preview, review, and send unit offers',
      icon: 'file-text',
      link: '/developer/offers',
      color: 'from-[#3A4E49] to-[#AA9072]',
      requiresOffersAccess: true,
    },
    {
      title: 'Missing Offer Requirements',
      description:
        'Review units missing floor plans, payment plans, or other required offer data',
      icon: 'triangle-alert',
      link: '/developer/offers/missing',
      color: 'from-[#AA9072] to-[#C6923B]',
      requiresOffersAccess: true,
      requiresMissingOfferRequirements: true,
    },
    {
      title: 'Phases',
      description: 'Review and manage project phases',
      icon: 'layers',
      link: '/developer/phases',
      color: 'from-[#6E9BAD] to-[#3A4E49]',
      requiresProjectAccess: true,
    },
    {
      title: 'Settings',
      description: 'Update company settings and workspace preferences',
      icon: 'settings',
      link: '/developer/settings',
      color: 'from-[#3A4E49] to-[#6E9BAD]',
      requiresSettingsAccess: true,
    },
    {
      title: 'Manage Properties',
      description: 'Edit and manage your property listings',
      icon: 'home',
      link: '/developer/properties',
      color: 'from-[#3A4E49] to-[#AA9072]',
    },
    {
      title: 'Upload Property',
      description: 'Add new properties to your portfolio',
      icon: 'upload',
      link: '/developer/properties/create',
      color: 'from-[#6E9BAD] to-[#3A4E49]',
      requiresPropertyCreate: true,
    },
    {
      title: 'Add New Project',
      description: 'Create a new project for your listings',
      icon: 'plus-circle',
      link: '/developer/projects/new',
      color: 'from-[#AA9072] to-[#6E9BAD]',
      requiresProjectWrite: true,
    },
  ];

  recentProjects: Array<{
    id: string;
    name: string;
    units: number;
    status: string;
    imageUrl?: string | null;
  }> = [];
  projectsPreviewLimit = 3;
  recentProperties: Array<DashboardProperty & { id: string }> = [];
  propertiesPreviewLimit = 3;
  projectPreviewImageFailed: Record<string, boolean> = {};
  propertyPreviewImageFailed: Record<string, boolean> = {};
  recentLeads = [
    { name: 'N/A', property: 'No lead data yet', date: '', status: 'new' },
  ];
  statusBuckets: Array<{
    label: string;
    value: string;
    key: 'available' | 'reserved' | 'reservedwithdeposit' | 'sold' | 'other';
    widthPct: number;
  }> = [];

  selectedStatusKey:
    'total' | 'available' | 'reserved' | 'reservedwithdeposit' | 'sold' | null =
    null;
  selectedStatusLabel = '';
  statusProperties: Property[] = [];
  statusLoading = false;
  statusError: string | null = null;
  exportingStatus = false;
  exportingStatusFormat: ReportExportFormat | null = null;
  private statusPropertySourceCache: Property[] | null = null;
  private statusPropertySourceSub: Subscription | null = null;
  partnerStats: Array<{
    label: string;
    primary: string;
    secondary?: string;
    helper?: string;
    icon: string;
  }> = [];
  salesTeamStats: Array<{
    label: string;
    primary: string;
    secondary?: string;
    helper?: string;
    icon: string;
  }> = [];
  portfolioStats: Array<{
    label: string;
    primary: string;
    secondary?: string;
    helper?: string;
    icon: string;
  }> = [];
  totalAssignedProperties = 0;

  partnerAssignedProperties = 0;
  salesTeamAssignedProperties = 0;
  missingOfferRequirementsCount = 0;
  postSaleSummary: PostSaleSummary | null = null;
  private propertyAccessScope: 'assigned' | 'projects' | 'all' = 'all';

  ngOnInit(): void {
    this.updateTrendLabel();
    this.userSub = this.user$.subscribe((user: any) => {
      const raw = user?.permission_codes ?? user?.permissionCodes ?? [];
      this.permissionCodes = Array.isArray(raw)
        ? raw.map((v: any) => String(v || '').trim())
        : [];
      this.actorRoleCode = String(
        user?.role_code || user?.roleCode || user?.role || '',
      )
        .trim()
        .toLowerCase();
      this.postSaleFollowUpEnabled = resolvePostSaleFollowUpEnabled(
        (user as any)?.feature_flags?.post_sale_follow_up_enabled,
      );
    });
    this.currencySub = this.currencyService.currencyChanges$.subscribe(() => {
      if (this.lastPayload) this.buildDashboardState(this.lastPayload);
    });
    this.loadData();
    this.loadReportProjects();
  }

  ngOnDestroy(): void {
    this.currencySub?.unsubscribe();
    this.userSub?.unsubscribe();
    this.statusPropertySourceSub?.unsubscribe();
  }

  setRange(range: '7d' | '30d' | 'ytd'): void {
    this.range = range;
    this.updateTrendLabel();
    this.loadData();
  }

  setProject(projectId: string): void {
    this.selectedProjectId = String(projectId || '').trim();
    this.clearSelection();
    this.loadData();
  }

  dashboardQueryParams(): Record<string, string> | null {
    return this.selectedProjectId ? { project: this.selectedProjectId } : null;
  }

  private loadData(): void {
    const requestVersion = ++this.dashboardRequestVersion;
    this.loading = true;
    this.dashboardError = null;
    this.lastPayload = null;
    this.stats = [];
    this.statusBuckets = [];
    this.recentProjects = [];
    this.recentProperties = [];
    this.recentLeads = [];
    this.statusPropertySourceCache = null;
    this.statusPropertySourceSub?.unsubscribe();
    this.statusPropertySourceSub = null;
    const params: { period?: string; from?: string; to?: string } = {};
    params.period = this.range;

    this.dashboardService.getDashboard({ ...params, projectId: this.selectedProjectId || null }).subscribe({
      next: (data) => {
        if (requestVersion !== this.dashboardRequestVersion) return;
        this.lastPayload = data;
        this.buildDashboardState(data);
        this.loading = false;
      },
      error: (error) => {
        if (requestVersion !== this.dashboardRequestVersion) return;
        this.dashboardError = error?.error?.message || 'Unable to load the dashboard.';
        this.recentProjects = [];
        this.recentProperties = [];
        this.stats = [];
        this.statusBuckets = [];
        this.recentLeads = [
          {
            name: 'N/A',
            property: 'No lead data yet',
            date: '',
            status: 'new',
          },
        ];
        this.loading = false;
      },
    });
    if (this.postSaleFollowUpEnabled && this.canReadPostSale()) {
      this.postSaleService.getSummary().subscribe({
        next: (summary) => {
          this.postSaleSummary = summary;
        },
        error: () => {
          this.postSaleSummary = null;
        },
      });
    } else {
      this.postSaleSummary = null;
    }
    this.loadMissingOfferRequirementsCount();
  }

  formatDateLabel(value: string | null | undefined): string {
    const raw = String(value || '').trim();
    if (!raw) return 'No due date';
    const date = new Date(raw);
    if (!Number.isFinite(date.getTime())) return raw;
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  private buildDashboardState(data: any): void {
    this.updateTrendLabel();
    this.propertyAccessScope = this.normalizePropertyAccessScope(
      data?.viewer?.property_access_scope,
    );

    const projects: DashboardProject[] = data?.projects?.items || [];
    this.recentProjects = projects
      .map((p: any) => ({
        id: String(p?.id || ''),
        name: p?.name || 'Untitled',
        units: Number(p?.propertyCount || 0) || 0,
        status: p?.status || 'Active',
        imageUrl: p?.imageUrl || null,
      }))
      .filter((p) => Boolean(p.id));
    this.projectsPreviewLimit = 3;

    const props: DashboardProperty[] = data?.properties?.items || [];
    this.recentProperties = Array.isArray(props)
      ? props
          .map((p: any) => ({
            ...p,
            id: p?.id ? String(p.id) : null,
          }))
          .filter((p: any) => Boolean(p?.id))
      : [];
    this.propertiesPreviewLimit = 3;
    const propsMeta: any = data?.properties || {};
    const statusCounts = propsMeta.statusCounts || {};
    const previousStatusCounts =
      propsMeta.previousStatusCounts || propsMeta.statusCountsPrevious || {};
    const statusTrends =
      propsMeta.statusTrends || propsMeta.statusTimeline || {};

    const available =
      statusCounts['available'] || statusCounts['Available'] || 0;
    const hold = statusCounts['hold'] || statusCounts['Hold'] || 0;
    const reservedWithDeposit =
      statusCounts['reservedwithdeposit'] ||
      statusCounts['ReservedWithDeposit'] ||
      0;
    const reserved =
      (statusCounts['reserved'] || statusCounts['Reserved'] || 0) + hold;
    const sold =
      (statusCounts['sold'] || statusCounts['Sold'] || 0) +
      (statusCounts['contracted'] || statusCounts['Contracted'] || 0);
    const rawTotal = propsMeta.total ?? props.length;
    const bucketSum = available + reserved + reservedWithDeposit + sold;
    const unpublishedCount = Math.max(0, rawTotal - bucketSum);
    const total = bucketSum; // Use bucketSum to exclude unpublished/other statuses

    const prevAvailable = Number(
      previousStatusCounts['available'] ??
        previousStatusCounts['Available'] ??
        0,
    );
    const prevHold = Number(
      previousStatusCounts['hold'] ?? previousStatusCounts['Hold'] ?? 0,
    );
    const prevReserved =
      Number(
        previousStatusCounts['reserved'] ??
          previousStatusCounts['Reserved'] ??
          0,
      ) + prevHold;
    const prevSold =
      Number(
        previousStatusCounts['sold'] ?? previousStatusCounts['Sold'] ?? 0,
      ) +
      Number(
        previousStatusCounts['contracted'] ??
          previousStatusCounts['Contracted'] ??
          0,
      );
    const prevTotal =
      propsMeta.previousTotal ?? this.sumCounts(previousStatusCounts);

    const totalAmount =
      this.sumConvertedAmounts(propsMeta.totalAmountsByCurrency) ??
      propsMeta.totalAmount ??
      null;
    const prevTotalAmount =
      this.sumConvertedAmounts(propsMeta.previousTotalAmountsByCurrency) ??
      propsMeta.previousTotalAmount ??
      null;
    const availableAmount =
      this.sumConvertedAmounts(propsMeta.statusAmountsByCurrency?.available) ??
      propsMeta.statusAmounts?.available ??
      propsMeta.statusAmounts?.Available ??
      null;
    const prevAvailableAmount =
      this.sumConvertedAmounts(
        propsMeta.previousStatusAmountsByCurrency?.available,
      ) ?? null;
    const reservedAmount =
      this.sumConvertedAmounts(
        this.mergeCurrencyBuckets(
          propsMeta.statusAmountsByCurrency?.reserved,
          propsMeta.statusAmountsByCurrency?.hold,
        ),
      ) ??
      (() => {
        const amounts = propsMeta.statusAmounts || {};
        const reserve = Number(amounts['reserved'] ?? amounts['Reserved'] ?? 0);
        const holdAmt = Number(amounts['hold'] ?? amounts['Hold'] ?? 0);
        const combined = reserve + holdAmt;
        return Number.isFinite(combined) ? combined : null;
      })();
    const prevReservedAmount =
      this.sumConvertedAmounts(
        this.mergeCurrencyBuckets(
          propsMeta.previousStatusAmountsByCurrency?.reserved,
          propsMeta.previousStatusAmountsByCurrency?.hold,
        ),
      ) ?? null;
    const reservedWithDepositAmount =
      this.sumConvertedAmounts(
        propsMeta.statusAmountsByCurrency?.reservedwithdeposit,
      ) ??
      (Number(
        propsMeta.statusAmounts?.['reservedwithdeposit'] ??
          propsMeta.statusAmounts?.['ReservedWithDeposit'] ??
          0,
      ) ||
        null);
    const prevReservedWithDepositAmount =
      this.sumConvertedAmounts(
        propsMeta.previousStatusAmountsByCurrency?.reservedwithdeposit,
      ) ?? null;
    const soldAmount =
      this.sumConvertedAmounts(
        this.mergeCurrencyBuckets(
          propsMeta.statusAmountsByCurrency?.sold,
          propsMeta.statusAmountsByCurrency?.contracted,
        ),
      ) ??
      (() => {
        const amounts = propsMeta.statusAmounts || {};
        const soldRaw = Number(amounts['sold'] ?? amounts['Sold'] ?? 0);
        const contractedRaw = Number(
          amounts['contracted'] ?? amounts['Contracted'] ?? 0,
        );
        const combined = soldRaw + contractedRaw;
        return Number.isFinite(combined) ? combined : null;
      })();
    const prevSoldAmount =
      this.sumConvertedAmounts(
        this.mergeCurrencyBuckets(
          propsMeta.previousStatusAmountsByCurrency?.sold,
          propsMeta.previousStatusAmountsByCurrency?.contracted,
        ),
      ) ?? null;

    const shareLabel = (
      value: number,
      denominator: number,
      tone: 'share' | 'absorption' = 'share',
    ): string => {
      if (!Number.isFinite(denominator) || denominator <= 0)
        return tone === 'absorption' ? '0% absorption' : '0% of portfolio';
      const pct = Math.round((value / denominator) * 100);
      return tone === 'absorption'
        ? `${pct}% absorption`
        : `${pct}% of portfolio`;
    };
    const totalInventoryLabel =
      totalAmount !== null
        ? `${total.toLocaleString()} units in portfolio`
        : `${total.toLocaleString()} visible units`;

    this.stats = [
      {
        label: 'Total Properties',
        statusKey: 'total',
        value: total,
        change: totalInventoryLabel,
        trend: 'neutral',
        metaLabel: 'Inventory',
        icon: 'home',
        link: '/developer/properties',
        amount: totalAmount,
        sparkline: this.buildTrendSnapshot(
          totalAmount ?? total,
          prevTotalAmount ?? prevTotal,
          statusTrends['total'],
        ).sparkline,
      },
      {
        label: 'Available',
        statusKey: 'available',
        value: available,
        change: shareLabel(available, total),
        trend: 'up',
        metaLabel: 'Portfolio share',
        icon: 'check',
        link: '/developer/properties',
        amount: availableAmount,
        sparkline: this.buildTrendSnapshot(
          availableAmount ?? available,
          prevAvailableAmount ?? prevAvailable,
          statusTrends['available'],
        ).sparkline,
      },
      {
        label: 'Reserved / Hold',
        statusKey: 'reserved',
        value: reserved,
        change: shareLabel(reserved, total),
        trend: 'neutral',
        metaLabel: 'Portfolio share',
        icon: 'calendar',
        link: '/developer/properties',
        amount: reservedAmount,
        sparkline: this.buildTrendSnapshot(
          reservedAmount ?? reserved,
          prevReservedAmount ?? prevReserved,
          statusTrends['reserved'],
        ).sparkline,
      },
      {
        label: 'Reserved with deposit',
        statusKey: 'reservedwithdeposit',
        value: reservedWithDeposit,
        change: shareLabel(reservedWithDeposit, total),
        trend: 'neutral',
        metaLabel: 'Portfolio share',
        icon: 'clock',
        link: '/developer/properties',
        amount: reservedWithDepositAmount,
        sparkline: this.buildTrendSnapshot(
          reservedWithDepositAmount ?? reservedWithDeposit,
          prevReservedWithDepositAmount ?? 0,
          statusTrends['reservedwithdeposit'],
        ).sparkline,
      },
      {
        label: 'Sold / Contracted',
        statusKey: 'sold',
        value: sold,
        change: shareLabel(sold, total, 'absorption'),
        trend: 'down',
        metaLabel: 'Absorption',
        icon: 'dollar-sign',
        link: '/developer/properties',
        amount: soldAmount,
        sparkline: this.buildTrendSnapshot(
          soldAmount ?? sold,
          prevSoldAmount ?? prevSold,
          statusTrends['sold'],
        ).sparkline,
      },
    ];
    // Zero-valued optional cards are omitted after a successful response. The
    // underlying API still returns the exact zero counts for reporting.
    this.stats = this.stats.filter((stat) => Number(stat.value) > 0);
    this.statusBuckets = [
      { label: 'Available', count: available, key: 'available' as const },
      { label: 'Reserved / Hold', count: reserved, key: 'reserved' as const },
      {
        label: 'Reserved with deposit',
        count: reservedWithDeposit,
        key: 'reservedwithdeposit' as const,
      },
      { label: 'Sold / Contracted', count: sold, key: 'sold' as const },
    ].filter((bucket) => bucket.count > 0).map((bucket) => ({
      label: bucket.label,
      value: `${bucket.count} properties`,
      key: bucket.key,
      widthPct: total > 0 ? (bucket.count / total) * 100 : 0,
    }));

    this.partnerStats = this.canSeePortfoliopartners()
      ? this.buildAssignmentPerformanceCards(data?.subpartners, {
          emptyAssignmentsHelper: 'No partner assignments yet.',
          includeAvgPricePerM2: true,
          hideAssignmentsHelper: true,
        })
      : [];
    this.partnerAssignedProperties = this.extractAssignedProperties(
      data?.subpartners,
    );
    this.salesTeamStats = this.canSeePortfolioSales()
      ? this.buildAssignmentPerformanceCards(data?.salesTeam, {
          emptyAssignmentsHelper: 'No team assignments yet.',
          includeAvgPricePerM2: true,
          assignmentsHelperLabel: 'team assignments',
        })
      : [];
    this.salesTeamAssignedProperties = this.extractAssignedProperties(
      data?.salesTeam,
    );
    this.portfolioStats = this.canSeePortfolioTotal()
      ? this.buildPortfolioPerformanceCards(data?.portfolio, propsMeta, {
          emptyAssignmentsHelper: 'No assignments yet.',
          hideAssignedProperties: this.shouldHideAssignedPropertiesMetric(),
        })
      : [];
    this.totalAssignedProperties = this.extractAssignedProperties(
      data?.portfolio,
    );

    const reservations = data?.reservations?.items || [];
    this.recentLeads = this.buildLeadsFromReservations(reservations);
    this.loading = false;
  }

  private buildAssignmentPerformanceCards(
    meta: any,
    options: {
      emptyAssignmentsHelper?: string;
      includeAvgPricePerM2?: boolean;
      includeAvgCommissionRate?: boolean;
      hideAssignedProperties?: boolean;
      assignmentsHelperLabel?: string;
      hideAssignmentsHelper?: boolean;
    } = {},
  ): Array<{
    label: string;
    primary: string;
    secondary?: string;
    helper?: string;
    icon: string;
  }> {
    const safe: any = meta && typeof meta === 'object' ? meta : {};
    const showCommission = this.canViewCommission();
    const assignedProperties = Number(safe.assignedProperties ?? 0) || 0;
    const assignments = Number(safe.assignments ?? 0) || 0;
    const totalValue =
      this.sumConvertedAmounts(safe.totalValueByCurrency) ?? null;
    const commission = showCommission
      ? (this.sumConvertedAmounts(safe.projectedCommissionByCurrency) ?? null)
      : null;
    const helperLabel =
      options.assignmentsHelperLabel ||
      'total assignments (team + partner grants)';
    const assignmentsHelper = options.hideAssignmentsHelper
      ? undefined
      : assignments
        ? `${assignments} ${helperLabel}`
        : options.emptyAssignmentsHelper || 'No assignments yet.';

    const cards: Array<{
      label: string;
      primary: string;
      secondary?: string;
      helper?: string;
      icon: string;
    }> = [];
    if (!options.hideAssignedProperties) {
      cards.push({
        label: 'Assigned Properties',
        primary: String(assignedProperties),
        secondary:
          totalValue !== null && totalValue > 0
            ? this.formatAmount(totalValue)
            : undefined,
        helper: assignmentsHelper,
        icon: 'link',
      });
    }

    if (showCommission) {
      const avgCommissionRaw =
        safe.avgCommissionPercent ??
        safe.avg_commission_percent ??
        safe.avgCommissionRate ??
        safe.avg_commission_rate ??
        null;
      const avgCommissionFromApi =
        avgCommissionRaw === null ||
        avgCommissionRaw === undefined ||
        avgCommissionRaw === ''
          ? null
          : (() => {
              const n = Number(avgCommissionRaw);
              return Number.isFinite(n) && n >= 0 ? n : null;
            })();

      const avgCommissionRate = options.includeAvgCommissionRate
        ? avgCommissionFromApi !== null
          ? avgCommissionFromApi
          : totalValue !== null && totalValue > 0 && commission !== null
            ? (commission / totalValue) * 100
            : null
        : null;

      const commissionRateLabel =
        avgCommissionRate !== null
          ? `Commission rate: ${avgCommissionRate.toFixed(2).replace(/\.?0+$/, '')}%`
          : null;
      const commissionHelper = assignments
        ? 'Based on assigned commission %.'
        : '—';
      const commissionHelperWithRate = commissionRateLabel
        ? commissionHelper && commissionHelper !== '—'
          ? `${commissionHelper}\n${commissionRateLabel}`
          : commissionRateLabel
        : commissionHelper;

      if (commission !== null && commission > 0) {
        cards.push({
          label: 'Projected Commission',
          primary: this.formatAmount(commission),
          helper: commissionHelperWithRate,
          icon: 'badge-percent',
        });
      }
    }

    if (options.includeAvgPricePerM2 ?? true) {
      const totalAreaM2 = Number(safe.totalAreaM2 ?? 0) || 0;
      const totalValueWithArea =
        this.sumConvertedAmounts(safe.totalValueWithAreaByCurrency) ?? null;
      const avgPricePerM2 =
        totalValueWithArea !== null && totalAreaM2 > 0
          ? totalValueWithArea / totalAreaM2
          : null;
      const areaLabel =
        totalAreaM2 > 0
          ? `${Math.round(totalAreaM2).toLocaleString()} m² usable area`
          : null;
      if (avgPricePerM2 !== null && avgPricePerM2 > 0) {
        cards.push({
          label: 'Avg Price / m²',
          primary: `${this.formatAmount(avgPricePerM2)} / m²`,
          helper: areaLabel || undefined,
          icon: 'maximize-2',
        });
      }
    }

    return cards;
  }

  private buildPortfolioPerformanceCards(
    assignmentMeta: any,
    propertiesMeta: any,
    options: {
      emptyAssignmentsHelper?: string;
      hideAssignedProperties?: boolean;
    } = {},
  ): Array<{
    label: string;
    primary: string;
    secondary?: string;
    helper?: string;
    icon: string;
  }> {
    // Portfolio performance cards are driven entirely by the backend portfolio rollup.
    // The propertiesMeta parameter is kept for API compatibility but no longer mixed in.
    return this.buildAssignmentPerformanceCards(assignmentMeta, {
      emptyAssignmentsHelper: options.emptyAssignmentsHelper,
      includeAvgPricePerM2: true,
      includeAvgCommissionRate: true,
      hideAssignedProperties: options.hideAssignedProperties,
    });
  }

  displayCompanyName(user: any): string {
    const name = String(user?.company_name || user?.companyName || '').trim();
    return name;
  }

  private updateTrendLabel(): void {
    if (this.range === '7d') this.trendLabel = 'vs last 7D';
    else if (this.range === '30d') this.trendLabel = 'vs last 30D';
    else this.trendLabel = 'YTD';
  }

  private mergeCurrencyBuckets(
    a?: Record<string, number> | null,
    b?: Record<string, number> | null,
  ): Record<string, number> | null {
    const out: Record<string, number> = {};
    const add = (src?: Record<string, number> | null) => {
      if (!src) return;
      Object.entries(src).forEach(([code, val]) => {
        const n = Number(val);
        if (!Number.isFinite(n)) return;
        out[code] = (out[code] || 0) + n;
      });
    };
    add(a);
    add(b);
    return Object.keys(out).length ? out : null;
  }

  private sumConvertedAmounts(
    amountsByCurrency?: Record<string, number> | null,
  ): number | null {
    if (!amountsByCurrency || typeof amountsByCurrency !== 'object')
      return null;
    let total = 0;
    let sawAny = false;
    for (const [code, raw] of Object.entries(amountsByCurrency)) {
      const amount = Number(raw);
      if (!Number.isFinite(amount)) continue;
      const from = String(code || '')
        .trim()
        .toUpperCase();
      total += this.currencyService.convert(amount, from);
      sawAny = true;
    }
    return sawAny ? total : null;
  }

  private permissionSet(): Set<string> {
    return permissionSetFromRaw(this.permissionCodes);
  }

  private ispartnerActor(): boolean {
    return this.actorRoleCode === 'subpartner';
  }

  private isSalesActor(): boolean {
    return this.actorRoleCode === 'sales';
  }

  private isAssigneeActor(): boolean {
    return this.ispartnerActor() || this.isSalesActor();
  }

  get visibleQuickActions(): Array<{
    title: string;
    description: string;
    icon: string;
    link: string;
    color: string;
    requiresPropertyCreate?: boolean;
    requiresLeadAccess?: boolean;
    requirespartnerAccess?: boolean;
    requiresSalesTeamAccess?: boolean;
    requiresProjectAccess?: boolean;
    requiresProjectWrite?: boolean;
    requiresOffersAccess?: boolean;
    requiresSettingsAccess?: boolean;
    requiresMissingOfferRequirements?: boolean;
  }> {
    return this.quickActions.filter((action) => {
      if (action.requiresPropertyCreate && !this.canCreateProperties())
        return false;
      if (action.requiresLeadAccess && !this.canManageLeads()) return false;
      if (action.requirespartnerAccess && !this.canManagepartners())
        return false;
      if (action.requiresSalesTeamAccess && !this.canManageSalesTeam())
        return false;
      if (action.requiresProjectAccess && !this.canReadProjects()) return false;
      if (action.requiresProjectWrite && !this.canWriteProjects()) return false;
      if (action.requiresOffersAccess && !this.canReadOffers()) return false;
      if (action.requiresSettingsAccess && !this.canReadSettings())
        return false;
      if (
        action.requiresMissingOfferRequirements &&
        !this.showMissingOfferRequirementsCta()
      )
        return false;
      return true;
    });
  }

  canCreateProperties(): boolean {
    return hasPropertyCreatePermission(this.permissionSet());
  }

  canSeePortfolioTotal(): boolean {
    const perms = this.permissionSet();
    if (this.ispartnerActor()) {
      return (
        perms.has('portfolio:read') ||
        perms.has('portfolio.total:read') ||
        perms.has('portfolio.subpartners:read') ||
        perms.has('workspace.portfolio:read')
      );
    }
    if (this.isSalesActor()) {
      return (
        perms.has('portfolio:read') ||
        perms.has('portfolio.total:read') ||
        perms.has('portfolio.sales:read') ||
        perms.has('workspace.portfolio:read')
      );
    }
    return (
      perms.has('portfolio:read') ||
      perms.has('portfolio.total:read') ||
      perms.has('workspace.portfolio:read')
    );
  }

  showPortfolioPerformanceSection(): boolean {
    if (!this.canSeePortfolioTotal()) return false;
    if (!this.portfolioStats.length) return false;
    if (!this.isAssigneeActor()) return true;
    if (this.isProjectAccessOnly()) return true;
    return this.totalAssignedProperties > 0;
  }

  canSeePortfoliopartners(): boolean {
    const perms = this.permissionSet();
    return perms.has('portfolio.subpartners:read');
  }

  /** "Lead visibility" toggle — the Recent Leads card must render only for members
   *  holding reservations:read (the API already empties the feed without it). */
  canViewRecentLeads(): boolean {
    return this.permissionSet().has('reservations:read');
  }

  canReadPostSale(): boolean {
    return this.permissionSet().has('post_sale:read');
  }

  canSeePortfolioSales(): boolean {
    const perms = this.permissionSet();
    return perms.has('portfolio.sales:read');
  }

  canViewCommission(): boolean {
    return canViewPortfolioCommission(this.permissionSet());
  }

  teamPerformanceDescription(): string {
    return this.canViewCommission()
      ? 'Assigned properties and projected commission performance.'
      : 'Assigned properties and average price per m² performance.';
  }

  partnerPerformanceDescription(): string {
    return this.canViewCommission()
      ? 'Assigned properties and projected commission performance.'
      : 'Assigned properties and average price per m² performance.';
  }

  showpartnerPerformanceSection(): boolean {
    return this.canSeePortfoliopartners() && this.partnerStats.length > 0;
  }

  showTeamPerformanceSection(): boolean {
    return this.canSeePortfolioSales() && this.salesTeamStats.length > 0;
  }

  canManagepartners(): boolean {
    return haspartnerWorkspaceAccessPermission(this.permissionSet());
  }

  canManageSalesTeam(): boolean {
    return hasSalesTeamWorkspaceAccessPermission(this.permissionSet());
  }

  canManageLeads(): boolean {
    const perms = this.permissionSet();
    return perms.has('reservations:read') || perms.has('reservations:write');
  }

  canReadProjects(): boolean {
    const perms = this.permissionSet();
    return perms.has('projects:read') || perms.has('projects:write');
  }

  canWriteProjects(): boolean {
    return this.permissionSet().has('projects:write');
  }

  canReadOffers(): boolean {
    const perms = this.permissionSet();
    return (
      perms.has('offers:read') ||
      perms.has('offers:write') ||
      perms.has('offers:send')
    );
  }

  canReadSettings(): boolean {
    const perms = this.permissionSet();
    return perms.has('org.settings:read') || perms.has('org.settings:write');
  }

  showMissingOfferRequirementsCta(): boolean {
    return this.canReadOffers() && this.missingOfferRequirementsCount > 0;
  }

  private extractAssignedProperties(meta: any): number {
    const safe: any = meta && typeof meta === 'object' ? meta : {};
    return Number(safe.assignedProperties ?? 0) || 0;
  }

  private normalizePropertyAccessScope(
    value: unknown,
  ): 'assigned' | 'projects' | 'all' {
    const raw = String(value || '')
      .trim()
      .toLowerCase();
    if (raw === 'projects') return 'projects';
    if (raw === 'all') return 'all';
    return 'assigned';
  }

  private isProjectAccessOnly(): boolean {
    return this.propertyAccessScope === 'projects';
  }

  private shouldHideAssignedPropertiesMetric(): boolean {
    return this.isAssigneeActor() && this.isProjectAccessOnly();
  }

  selectStatus(stat: (typeof this.stats)[number]): void {
    if (this.selectedStatusKey === stat.statusKey) {
      this.clearSelection();
      return;
    }
    this.selectedStatusKey = stat.statusKey;
    this.selectedStatusLabel = stat.label;
    this.fetchStatusProperties(stat.statusKey);
    this.scrollToStatusSection();
  }

  clearSelection(): void {
    this.selectedStatusKey = null;
    this.selectedStatusLabel = '';
    this.statusProperties = [];
    this.statusLoading = false;
    this.statusError = null;
  }

  async exportStatusProperties(format: ReportExportFormat = 'csv'): Promise<void> {
    if (this.exportingStatus) return;
    if (!this.statusProperties.length) return;
    this.exportingStatus = true;
    this.exportingStatusFormat = format;
    try {
      const header = [
        'Property ID',
        'Unit',
        'Title',
        'Project',
        'Phase',
        'Status',
        'Bedrooms',
        'Area',
        'Assigning',
        'Price',
        'Property link',
      ];
      const numbered = prependRowNumbers(
        header,
        this.statusProperties.map((p) => {
          const badge = propertyStatusKey(p.statusBadge || p.status || '');
          const canSharePublic = badge !== 'draft' && badge !== 'unpublished';
          return [
            p.id,
            p.unitNumber,
            p.title,
            p.projectName,
            p.phaseName,
            this.statusLabel(p),
            this.bedroomsLabel(p),
            this.formatPropertyArea(p),
            this.statusAssigningLabel(p),
            this.formatPropertyPrice(p),
            canSharePublic ? buildPropertyLink(p.id, 'public') : '',
          ];
        }),
      );
      const suffix = (this.selectedStatusLabel || 'status')
        .toLowerCase()
        .replace(/\s+/g, '-');
      const base = `nibni-properties-${suffix}-${new Date().toISOString().slice(0, 10)}`;
      if (format === 'xlsx') {
        await downloadXlsx(
          `${base}.xlsx`,
          'Properties',
          numbered.header,
          numbered.rows,
        );
      } else if (format === 'pdf') {
        await downloadPdfReport(`${base}.pdf`, `${this.selectedStatusLabel || 'Properties'} Report`, numbered.header, numbered.rows);
      } else {
        downloadCsv(`${base}.csv`, numbered.header, numbered.rows);
      }
    } finally {
      this.exportingStatus = false;
      this.exportingStatusFormat = null;
    }
  }

  showMoreProjects(): void {
    this.projectsPreviewLimit = Math.min(
      this.projectsPreviewLimit + 3,
      this.recentProjects.length,
    );
  }

  showMoreProperties(): void {
    this.propertiesPreviewLimit = Math.min(
      this.propertiesPreviewLimit + 3,
      this.recentProperties.length,
    );
  }

  private scrollToStatusSection(): void {
    setTimeout(() => {
      const el = this.statusSectionRef?.nativeElement;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  private fetchStatusProperties(
    statusKey:
      'total' | 'available' | 'reserved' | 'reservedwithdeposit' | 'sold',
  ): void {
    this.statusLoading = true;
    this.statusError = null;
    this.statusProperties = [];

    if (this.statusPropertySourceCache) {
      this.applyStatusPropertiesFromSource(
        statusKey,
        this.statusPropertySourceCache,
      );
      return;
    }

    if (this.statusPropertySourceSub) return;

    this.statusPropertySourceSub = this.propertiesService
      .getPropertiesAuth(
        this.selectedProjectId ? { project_id: this.selectedProjectId } : {},
        { page: 1, perPage: 200 },
      )
      .subscribe({
        next: ({ items }) => {
          this.statusPropertySourceCache = items;
          this.statusPropertySourceSub = null;
          this.applyStatusPropertiesFromSource(
            this.selectedStatusKey ?? statusKey,
            items,
          );
        },
        error: (err) => {
          this.statusPropertySourceSub = null;
          this.statusLoading = false;
          this.statusError = err?.message || 'Failed to load properties';
        },
      });
  }

  private applyStatusPropertiesFromSource(
    statusKey:
      'total' | 'available' | 'reserved' | 'reservedwithdeposit' | 'sold',
    items: Property[],
  ): void {
    const filtered = items.filter((p) => this.matchesStatus(statusKey, p));
    this.statusProperties = filtered.slice(0, 50);
    this.statusLoading = false;
  }

  private matchesStatus(
    key: 'total' | 'available' | 'reserved' | 'reservedwithdeposit' | 'sold',
    p: Property,
  ): boolean {
    if (key === 'total') return true;
    const badge = propertyStatusKey(p.statusBadge || p.status || '');
    if (key === 'available') return badge === 'available';
    if (key === 'reserved') return badge === 'reserved';
    if (key === 'reservedwithdeposit') return badge === 'reservedwithdeposit';
    if (key === 'sold') return badge === 'sold' || badge === 'contracted';
    return true;
  }

  private statusAssigningLabel(property: Property): string {
    const company = String((property as any)?.assignedCompanyName || '').trim();
    const person = String((property as any)?.assignedToName || '').trim();
    if (company && person && company.toLowerCase() !== person.toLowerCase()) {
      return `${company} · ${person}`;
    }
    return company || person || '';
  }

  statusLabel(p: { status?: any; statusBadge?: any }): string {
    const raw = p.statusBadge || p.status || '';
    const normalized = propertyStatusKey(raw);
    if (
      normalized === 'sold' &&
      String(raw || '')
        .trim()
        .toLowerCase() === 'Contracted'
    )
      return 'Contracted';
    return propertyStatusLabel(raw);
  }

  bedroomsLabel(p: { bedrooms?: any }): string {
    const raw = typeof p?.bedrooms === 'string' ? Number(p.bedrooms) : p?.bedrooms;
    if (raw === null || raw === undefined || !Number.isFinite(Number(raw))) return '—';
    const n = Number(raw);
    return n === 0 ? 'Studio' : String(n);
  }

  formatPropertyArea(p: {
    bua?: any;
    buaUnit?: any;
    totalArea?: any;
    totalAreaUnit?: any;
  }): string {
    const num = (v: any): number | null => {
      const n = typeof v === 'string' ? Number(v) : v;
      return n === null || n === undefined || !Number.isFinite(Number(n)) ? null : Number(n);
    };
    const value = num(p?.bua) ?? num(p?.totalArea);
    if (value === null) return '—';
    const unit = String(p?.buaUnit || p?.totalAreaUnit || 'SQM').trim() || 'SQM';
    const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
    return `${formatted} ${unit}`;
  }

  formatPropertyPrice(p: {
    price?: any;
    currency?: any;
    currencyInfo?: any;
    currencyCode?: any;
  }): string {
    const raw = p?.price;
    const rawNumber = typeof raw === 'string' ? Number(raw) : raw;
    if (
      rawNumber === null ||
      rawNumber === undefined ||
      !Number.isFinite(Number(rawNumber))
    )
      return '—';
    const fromCode = (
      p.currencyCode ||
      p.currency ||
      p.currencyInfo?.code ||
      'USD'
    )
      .toString()
      .toUpperCase();
    const display = this.currencyService.convertForDisplay(Number(rawNumber), fromCode);
    return this.currencyService.format(
      display.amount,
      display.currency,
    );
  }

  propertyPreviewImage(p: DashboardProperty): string | null {
    return (p?.imageThumb || p?.imageUrl || null) as string | null;
  }

  onProjectPreviewImageError(projectId: string): void {
    if (!projectId) return;
    this.projectPreviewImageFailed[projectId] = true;
  }

  onPropertyPreviewImageError(propertyId: string): void {
    if (!propertyId) return;
    this.propertyPreviewImageFailed[propertyId] = true;
  }

  goToProperty(propertyId: string): void {
    if (!propertyId) return;
    this.router
      .navigate(['/developer/properties/view', propertyId], {
        queryParams: this.dashboardQueryParams() || undefined,
      })
      .catch(() => {});
  }

  private buildLeadsFromReservations(
    reservations: DashboardReservation[],
  ): Array<{ name: string; property: string; date: string; status: string }> {
    return reservations.slice(0, 3).map((r) => ({
      name: r.customerName || 'Unknown',
      property: r.propertyLabel || r.propertyId || '—',
      date: r.createdTime || new Date().toISOString(),
      status: normalizeLeadStatus(r.status),
    }));
  }

  sparkHeight(value: number, series: number[] = []): string {
    if (!series.length) return '12px';
    const max = Math.max(...series, 1);
    const ratio = value / max;
    const clamped = Math.max(0.12, Math.min(1, ratio));
    return `${Math.round(clamped * 100)}%`;
  }

  sparkColor(trend: 'up' | 'down' | 'neutral' = 'neutral'): string {
    if (trend === 'up') return '#2F8F68';
    if (trend === 'down') return '#C65A5A';
    return '#3A4E49';
  }

  // Single source of truth for the dashboard cards' status colours: the chip
  // text/background/border, the distribution bar segments and the card badges
  // all derive from this (see statusSurfaceByKey / statusBorderByKey /
  // cardBadgeStyle / statBackground).
  //
  // Sold and Contracted share the SAME gold, because the "Sold" bucket on this
  // dashboard is itself sold + contracted (see the `sold` computation above) —
  // they are one closed-deal state here, so one colour. That colour is the
  // nibni brand gold (--nibni-light-taupe #AA9072, aliased as --v2-gold in
  // v2.theme.scss), matching the rest of the app. Reserved / Hold is red: it is
  // the state that needs attention, and it previously owned the old amber gold.
  statusAccentByKey(key: string): string {
    switch (
      String(key || '')
        .trim()
        .toLowerCase()
    ) {
      case 'available':
        return '#2F8F68';
      case 'reserved':
        return '#C65A5A';
      case 'reservedwithdeposit':
        return '#D09A3A';
      case 'sold':
      case 'contracted':
        return '#AA9072';
      case 'total':
        return '#6E9BAD';
      default:
        return '#3A4E49';
    }
  }

  statusSurfaceByKey(key: string): string {
    return this.hexToRgba(this.statusAccentByKey(key), 0.12);
  }

  statusBorderByKey(key: string): string {
    return this.hexToRgba(this.statusAccentByKey(key), 0.22);
  }

  statAccent(stat: {
    icon?: string;
    label?: string;
    statusKey?: string;
  }): string {
    return this.statusAccentByKey(
      (stat as any)?.statusKey || stat.icon || stat.label || '',
    );
  }

  trendBadgeStyle(trend: 'up' | 'down' | 'neutral'): Record<string, string> {
    const accent =
      trend === 'up' ? '#2F8F68' : trend === 'down' ? '#C65A5A' : '#3A4E49';
    return {
      background: this.hexToRgba(accent, 0.14),
      color: accent,
      borderColor: this.hexToRgba(accent, 0.24),
    };
  }

  cardBadgeStyle(stat: {
    statusKey?: string;
    trend?: 'up' | 'down' | 'neutral';
  }): Record<string, string> {
    const accent = this.statusAccentByKey(String(stat?.statusKey || ''));
    return {
      background: this.hexToRgba(accent, 0.14),
      color: accent,
      borderColor: this.hexToRgba(accent, 0.24),
    };
  }

  statBackground(stat: { icon: string; label: string }): string {
    const accent = this.statAccent(stat);
    const a1 = this.hexToRgba(accent, 0.18);
    const a2 = this.hexToRgba(accent, 0.08);
    return `radial-gradient(120% 90% at 14% 20%, ${a1} 0%, transparent 62%), radial-gradient(120% 90% at 86% 110%, ${a2} 0%, transparent 65%)`;
  }

  statAccentBg(stat: { icon: string; label: string }): string {
    return this.hexToRgba(this.statAccent(stat), 0.14);
  }

  statAccentBorder(stat: { icon: string; label: string }): string {
    return this.hexToRgba(this.statAccent(stat), 0.22);
  }

  private loadMissingOfferRequirementsCount(): void {
    if (!this.canReadOffers()) {
      this.missingOfferRequirementsCount = 0;
      return;
    }
    this.offersService
      .listUnitOffers({ page: 1, perPage: 1, offerReady: false })
      .subscribe({
        next: ({ total }) => {
          this.missingOfferRequirementsCount = Number.isFinite(total)
            ? Number(total)
            : 0;
        },
        error: () => {
          this.missingOfferRequirementsCount = 0;
        },
      });
  }

  private hexToRgba(hex: string, alpha: number): string {
    const normalized = hex.replace('#', '').trim();
    const full =
      normalized.length === 3
        ? normalized
            .split('')
            .map((c) => c + c)
            .join('')
        : normalized;
    if (full.length !== 6) return `rgba(110,155,173,${alpha})`;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private sparkPoints(
    series?: number[] | null,
    width = 100,
    height = 36,
    padding = 3,
  ): Array<{ x: number; y: number }> {
    const values = (series || [])
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v));
    if (!values.length) return [];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const usableWidth = width - padding * 2;
    const usableHeight = height - padding * 2;
    const step =
      values.length > 1 ? usableWidth / (values.length - 1) : usableWidth;
    return values.map((v, idx) => {
      const x = padding + idx * step;
      const ratio = (v - min) / span;
      const y = padding + (1 - ratio) * usableHeight;
      return { x, y };
    });
  }

  sparkLinePoints(series?: number[] | null): string {
    const pts = this.sparkPoints(series);
    return pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  }

  sparkAreaPath(series?: number[] | null): string {
    const width = 100;
    const height = 36;
    const padding = 3;
    const pts = this.sparkPoints(series, width, height, padding);
    if (!pts.length) return '';
    const baseline = height - padding;
    const line = pts
      .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(' L ');
    return `M ${pts[0].x.toFixed(1)},${baseline.toFixed(1)} L ${line} L ${pts[pts.length - 1].x.toFixed(1)},${baseline.toFixed(1)} Z`;
  }

  chartSeries(stat: {
    sparkline?: number[] | null;
    value?: number | string;
  }): number[] {
    const series = (stat.sparkline || [])
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v));
    if (series.length >= 2) return series;
    const base = Number(stat.value);
    const safeBase = Number.isFinite(base) && base !== 0 ? base : 10;
    return [
      safeBase * 0.6,
      safeBase * 0.65,
      safeBase * 0.62,
      safeBase * 0.7,
      safeBase * 0.68,
      safeBase * 0.76,
    ];
  }

  cardChartLinePoints(series?: number[] | null): string {
    const pts = this.sparkPoints(series, 100, 100, 6);
    return pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  }

  cardChartAreaPath(series?: number[] | null): string {
    const width = 100;
    const height = 100;
    const padding = 6;
    const pts = this.sparkPoints(series, width, height, padding);
    if (!pts.length) return '';
    const baseline = height - padding;
    const line = pts
      .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(' L ');
    return `M ${pts[0].x.toFixed(1)},${baseline.toFixed(1)} L ${line} L ${pts[pts.length - 1].x.toFixed(1)},${baseline.toFixed(1)} Z`;
  }

  formatAmount(value: number | null | undefined): string {
    if (value === null || value === undefined) return '';
    const safe = Number(value);
    if (!Number.isFinite(safe)) return '';
    return this.formatCompactCurrency(
      safe,
      this.currencyService.currentCurrency(),
    );
  }

  private formatCompactCurrency(value: number, currency: string): string {
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    const format = (n: number) => {
      const fixed = n.toFixed(1);
      return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
    };
    if (abs >= 1_000_000_000)
      return `${sign}${currency} ${format(abs / 1_000_000_000)}B`;
    if (abs >= 1_000_000)
      return `${sign}${currency} ${format(abs / 1_000_000)}M`;
    if (abs >= 1_000) return `${sign}${currency} ${format(abs / 1_000)}K`;
    return `${sign}${currency} ${format(abs)}`;
  }

  leadStatusStyle(status: string): Record<string, string> {
    const normalized = normalizeLeadStatus(status);
    let accent = '#3A4E49';
    if (normalized === 'new' || normalized === 'scheduled_calls')
      accent = '#6E9BAD';
    else if (normalized === 'approached' || normalized === 'no_answer')
      accent = '#AA9072';
    else if (
      normalized === 'qualified' ||
      normalized === 'reserved' ||
      normalized === 'reserved_with_deposit'
    )
      accent = '#B88746';
    else if (
      normalized === 'sold' ||
      normalized === 'contracted' ||
      normalized === 'not_interested'
    )
      accent = '#3A4E49';

    return {
      background: this.hexToRgba(accent, 0.14),
      color: accent,
      borderColor: this.hexToRgba(accent, 0.22),
    };
  }

  leadStatusLabel(status: string | null | undefined): string {
    return leadStatusLabel(status);
  }

  formatDate(value: string): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private sumCounts(counts: Record<string, number>): number {
    return Object.values(counts || {}).reduce(
      (acc, val) => acc + (Number.isFinite(val) ? Number(val) : 0),
      0,
    );
  }

  private buildTrendSnapshot(
    current: number,
    previous?: number | null,
    history?: number[],
  ): { change: string; trend: 'up' | 'down' | 'neutral'; sparkline: number[] } {
    const sanitizedSeries = this.sanitizeSeries(history);
    const series = sanitizedSeries.length ? sanitizedSeries : [];
    const prev = Number.isFinite(previous as number)
      ? (previous as number)
      : series.length >= 2
        ? series[series.length - 2]
        : null;
    let trend: 'up' | 'down' | 'neutral' = 'neutral';
    let change = 'No change';
    if (prev !== null) {
      if (prev === 0) {
        if (current > 0) {
          trend = 'up';
          change = 'New';
        }
      } else {
        const deltaPct = ((current - prev) / prev) * 100;
        if (Number.isFinite(deltaPct)) {
          trend = deltaPct > 0 ? 'up' : deltaPct < 0 ? 'down' : 'neutral';
          change = `${deltaPct > 0 ? '+' : ''}${deltaPct.toFixed(1)}%`;
        }
      }
    }
    const sparkline = (
      series.length
        ? series.slice(-6)
        : prev !== null
          ? [prev, current]
          : [Math.max(0, current - 2), current]
    ) as number[];
    return { change, trend, sparkline };
  }

  private sanitizeSeries(series?: any): number[] {
    if (!Array.isArray(series)) return [];
    return series
      .map((val) => Number(val))
      .filter((val) => Number.isFinite(val) && val >= 0);
  }

  // Partner Performance Methods
  canExportReports(): boolean {
    return (
      this.permissionCodes.includes('reports:export') ||
      this.permissionCodes.includes('users:write')
    );
  }

  showPropertyStatusBadge(property: { status?: unknown }): boolean {
    return shouldShowPropertyStatusBadge(
      property.status,
      this.actorRoleCode === 'app_admin',
    );
  }

  exportingTeamPerformance = false;
  exportingPartnerStats = false;
  exportingTeamPerformanceFormat: ReportExportFormat | null = null;
  exportingPartnerStatsFormat: ReportExportFormat | null = null;
  exportingFullReportFormat: ReportExportFormat | null = null;

  private loadReportProjects(): void {
    this.reportProjectsLoading = true;
    this.projectsService.getProjectsAuth({ page: 1, perPage: 100 }).subscribe({
      next: (result) => {
        this.reportProjects = [...(result?.items || [])].sort((a, b) =>
          String(a?.name || '').localeCompare(String(b?.name || ''), undefined, {
            numeric: true,
            sensitivity: 'base',
          }),
        );
        this.reportProjectsLoading = false;
      },
      error: () => {
        this.reportProjects = [];
        this.reportProjectsLoading = false;
      },
    });
  }

  async exportFullReport(format: ReportExportFormat): Promise<void> {
    if (!this.canExportReports() || this.exportingFullReportFormat) return;
    this.exportingFullReportFormat = format;
    try {
      await this.reportExports.download('full', format, { projectId: this.reportProjectId });
    } catch (err) {
      console.warn('[dashboard] failed to export full report:', err);
    } finally {
      this.exportingFullReportFormat = null;
    }
  }

  async exportPartnerStats(format: ReportExportFormat): Promise<void> {
    if (!this.canExportReports() || this.exportingPartnerStats) return;
    this.exportingPartnerStats = true;
    this.exportingPartnerStatsFormat = format;
    try {
      const stats = this.partnerStats;
      if (!stats.length) return;
      const header = ['Metric', 'Value', 'Detail'];
      const rows: string[][] = [];
      for (const s of stats) {
        rows.push([
          String(s.label || ''),
          String(s.primary || ''),
          String(s.secondary || s.helper || ''),
        ]);
      }
      const rangeLabel =
        this.range === '7d' ? '7d' : this.range === '30d' ? '30d' : 'ytd';
      const base = `partner-performance-${rangeLabel}-${new Date().toISOString().slice(0, 10)}`;
      if (format === 'pdf') await downloadPdfReport(`${base}.pdf`, 'Partner Performance', header, rows);
      else if (format === 'xlsx') await downloadXlsx(`${base}.xlsx`, 'Partner Performance', header, rows);
      else downloadCsv(`${base}.csv`, header, rows);
    } catch (err) {
      console.warn('[dashboard] failed to export partner performance:', err);
    } finally {
      this.exportingPartnerStats = false;
      this.exportingPartnerStatsFormat = null;
    }
  }

  async exportTeamPerformance(format: ReportExportFormat): Promise<void> {
    if (!this.canExportReports() || this.exportingTeamPerformance) return;
    this.exportingTeamPerformance = true;
    this.exportingTeamPerformanceFormat = format;
    try {
      const stats = this.salesTeamStats;
      if (!stats.length) return;
      const header = ['Metric', 'Value', 'Detail'];
      const rows: string[][] = [];
      for (const s of stats) {
        rows.push([
          String(s.label || ''),
          String(s.primary || ''),
          String(s.secondary || s.helper || ''),
        ]);
      }
      const rangeLabel =
        this.range === '7d' ? '7d' : this.range === '30d' ? '30d' : 'ytd';
      const base = `team-performance-${rangeLabel}-${new Date().toISOString().slice(0, 10)}`;
      if (format === 'pdf') await downloadPdfReport(`${base}.pdf`, 'Team Performance', header, rows);
      else if (format === 'xlsx') await downloadXlsx(`${base}.xlsx`, 'Team Performance', header, rows);
      else downloadCsv(`${base}.csv`, header, rows);
    } catch (err) {
      console.warn('[dashboard] failed to export team performance:', err);
    } finally {
      this.exportingTeamPerformance = false;
      this.exportingTeamPerformanceFormat = null;
    }
  }
}
